/*******************************************************************************
 * Copyright     : Copyright (c)  2025 Honeywell Inc. All rights reserved.
 *                 This work contains trade secrets and confidential materials
 *                 of Honeywell Inc.No part may be reproduced or transmitted in any form by any
 *                 means or for anypurpose without the express written permission of Honeywell. *
 *******************************************************************************/
import React, { useState, useEffect, useRef } from "react";

//@Packages
import { useTranslation } from "react-i18next";
import { Modal } from "@forge/common";
import { useFlags } from "launchdarkly-react-client-sdk";

//@Components
import NiagaraServiceDetails from "../../niagara";
import Next9ServiceDetails from "../../next9";
import FcCustomApp from "../../customApp";
import ServiceLogsComponent from "../../ServiceLogs";
import ConfirmationModal from "../../../../../reusablecomponents/confirmationmodal";

//@Models
import { IServiceActionComponentProps, IFormData } from "../../../../../models/softwaredetails";

//@Utils
import { SOFTWARE_CONSTANTS, BASE_SOFTWARES, ServiceActions, ServiceKeys, LicenseStatus } from "../../../../../utils/constants";
import { useNotificationUtil } from "../../../utils/NotificationUtils";

//@Styles
import "./index.scss";

const ServiceActionComponent: React.FC<IServiceActionComponentProps> = (props) => {
  const { t } = useTranslation();
  const flags = useFlags();
  
  const [openInstallationForm, setOpenInstallationForm] = useState(false);
  const [showServiceActionComponent, setShowServiceActionComponent] = useState(true);
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [showConfirmUnInstallModal, setShowConfirmUnInstallModal] = useState(false);
  const [showLogsModal, setShowLogsModal] = useState(false);
  const [logs, setLogs] = useState<string[]>([]);
  const [fieldProps, setFieldProps] = useState({});
  const [localConfig, setLocalConfig] = useState({});
  const [modalKey, setModalKey] = useState(0);

  const { showWarningNotification,
          notificationProps, 
          evaluateWarningNotification, 
          resetNotification 
        } = useNotificationUtil();

useEffect(() => {
  if (!props.actionKey) return;
  
  // Then execute action
  processPreMountCondition();
  serviceAction();
  evaluateWarningNotification(props.actionKey, props.softwareKey);
  
  return () => {
    setOpenInstallationForm(false);
    resetNotification();
  };
}, [props.actionKey, props.softwareKey, props.actionTrigger]);

  const renderServiceAction = () => {
    if (showServiceActionComponent) {
      return (
        <React.Fragment>
          {renderInstallationForm()}
          {renderConfirmUnInstall()}
          {renderLogsModal()}
        </React.Fragment>
      );
    }
    return <div></div>;
  };

  const serviceAction = () => {
    if (props.actionKey === ServiceActions.INSTALL) {
      installAction();
    } else if (props.actionKey === ServiceActions.UNINSTALL) {
      unInstallAction();
    } else if (props.actionKey === ServiceActions.REINSTALL) {
      setFieldProps(getFieldProps(props.softwareKey));
      reInstallAction();
    } else if (props.actionKey === ServiceActions.VIEWLOGS) {
    viewLogsAction();
  }
  };

const renderInstallationForm = () => {
    const isModalOpen = showServiceModal || serviceModelStatus();
    
    return (
      <>
        {isModalOpen && (
          <Modal
            backdrop
            title={props.actionKey === ServiceActions.INSTALL ? 
              t(SOFTWARE_CONSTANTS.installSoftware) : 
              t(SOFTWARE_CONSTANTS.reinstallSoftware)
            }
            onModalClose={closeInstallationForm}
            onBackdropClick={closeInstallationForm}
            className="service-component-modal"
          >
            <div className="service-component-content">
              {getSoftwareComponent()}
            </div>
          </Modal>
        )}
      </>
    );
  };

    const renderConfirmUnInstall = () => {
    return (
      <ConfirmationModal
        isOpen={showConfirmUnInstallModal}
        onClose={() => setShowConfirmUnInstallModal(false)}
        onConfirm={triggerUnInstall}
        title={t(SOFTWARE_CONSTANTS.confirmAction)}
        content={`${t(SOFTWARE_CONSTANTS.confirmUninstallMessage)} ${props.softwareName}?`}
        footerLeftText={SOFTWARE_CONSTANTS.cancel}
        footerRightText={SOFTWARE_CONSTANTS.uninstall}
      />
    );
  };

  const installAction = () => {
    if (props.softwareKey === ServiceKeys.NIAGARA ||
        (props.softwareKey === ServiceKeys.NEXT9 && 
         (flags.vseAutoLicense === false || openInstallationForm === true))) {
      setShowServiceModal(true);
    } else if (props.softwareKey === ServiceKeys.DISPATCH || 
               (props.softwareKey === ServiceKeys.NEXT9 && 
                flags.vseAutoLicense === true)) {
      props.installService?.(props.softwareKey, null);
    } else {
      setShowServiceModal(true);
    }
  };

  const unInstallAction = () => {
    setShowConfirmUnInstallModal(true);
  };

  const reInstallAction = () => {
    if (props.softwareKey === ServiceKeys.DISPATCH) {
      props.reinstallService!(props.softwareKey, null);
    } else {
      setShowServiceModal(true);
    }
  };

  const viewLogsAction = () => {
  // Call API to fetch logs
    if (props.fetchServiceLogs) {
      props.fetchServiceLogs(props.softwareKey).then(response => {
        if (response && response.data) {
          setLogs(response.data);
        }
      });
    }
  setShowLogsModal(true);
};

  const serviceModelStatus = () => {
    if (props.softwareKey === ServiceKeys.NEXT9 && openInstallationForm === true) {
      return true;
    }
    return false;
  };

const closeInstallationForm = () => {
  setShowServiceModal(false);
  setOpenInstallationForm(false);
  setModalKey(prev => prev + 1);
  props.refreshTableData!();
};

  const getSoftwareComponent = () => {
    const key = props.softwareKey;
    
    if (key === ServiceKeys.NIAGARA) {
      return (
        <NiagaraServiceDetails
          title="Niagara" 
          subTitle={t("Enter Niagara Platform details below.")} 
          key={`niagara-${modalKey}`}
          config={{}} 
          onCancel={closeInstallationForm} 
          softwareId={props.softwareId}
          softwareKey={props.softwareKey}
          gatewayType={props.gatewayType} 
          onSubmit={(formData) => onServiceSubmit(formData)} 
          cancelEnabled={true}
        />
      );
    } else if (key === ServiceKeys.NEXT9) {
      return (
        <Next9ServiceDetails 
          title="HFCS VSE" 
          subTitle={t("Enter HFCS VSE details below.")} 
          key={`next9-${modalKey}`}
          config={localConfig} 
          onCancel={closeInstallationForm} 
          onSubmit={(formData) => onServiceSubmit(formData)} 
          fieldProps={fieldProps} 
          cancelEnabled={true}
          showWarningMessage={showWarningNotification} 
          notificationProps={notificationProps}
        />
      );
    } else {
      return (
        <FcCustomApp 
            title={`Enter ${props.softwareName || "App"} Details`}
            subTitle={t("Enter application configuration details below.")}
            config={{}}
            onCancel={closeInstallationForm}
            onSubmit={(formData) => onfcAppServiceSubmit(formData)}
            cancelEnabled={true}
            softwareKey={props.softwareKey}
            getSoftwareVersions={props.getSoftwareVersions}
            getAppConfig={props.getAppConfig}
            showWarningMessage={showWarningNotification}
            notificationProps={notificationProps}
            fieldProps={fieldProps}
        />
      );
    }
  };

  const onServiceSubmit = (formData: IFormData) => {
    if (props.actionKey === ServiceActions.INSTALL) {
      props.installService!(props.softwareKey, formData);
    } else {
      props.reinstallService!(props.softwareKey, formData);
    }
    closeInstallationForm();
  };

  const onfcAppServiceSubmit = (formData: IFormData) => {
    props.fcAppInstallService!(props.softwareKey, formData);
    closeInstallationForm();
  };

  const triggerUnInstall = () => {
    setShowConfirmUnInstallModal(false);
    if (BASE_SOFTWARES.includes(props.softwareKey)) {
      props.unInstallService!(props.softwareKey);
    } else {
      props.fcAppUnInstallService!(props.softwareKey);
    }
  };

  const getFieldProps = (serviceKey: string) => {
    let fieldProperties = {};
    if (serviceKey === ServiceKeys.NEXT9) {
      fieldProperties = {
        licenseKey: {
          disabled: false
        },
        siteId: {
          disabled: false
        }
      };
    }
    return fieldProperties;
  };

  const renderLogsModal = () => {
    return (
      <ServiceLogsComponent 
        isOpen={showLogsModal}
        showLogsLoader={props.showLogsLoader} 
        fetchServiceLogs={props.fetchServiceLogs} 
        serviceKey={props.softwareKey} 
        serviceLogs={logs} 
        onClose={() => setShowLogsModal(false)}
      />
    );
  };

  const processPreMountCondition = () => {
    populateSoftwareDetails();
  };

  const populateSoftwareDetails = () => {
    if (props.softwareKey === ServiceKeys.NEXT9 && 
        (props.actionKey === ServiceActions.REINSTALL ||
         (props.actionKey === ServiceActions.INSTALL && !flags.vseAutoLicense))) {
      setShowServiceActionComponent(false);
      let status = null;
      if (props.actionKey === ServiceActions.REINSTALL) {
        status = LicenseStatus.INUSE;
      } else if (props.actionKey === ServiceActions.INSTALL) {
        status = LicenseStatus.ASSIGNED;
      }
      
      props.getSoftwareDetails!(props.softwareKey, status!)
      .then((response) => {
        if (response) {
          setLocalConfig(response);
        } else {
          setLocalConfig({});
        }
        setShowServiceActionComponent(true);
      })
      .catch((error) => {
        setLocalConfig({});
        setShowServiceActionComponent(true);
      });
    } else {
      setShowServiceActionComponent(true);
    }
  };

  return renderServiceAction();
};

export default ServiceActionComponent;